import { nanoid } from 'nanoid';
import { addHours, addMinutes, format, parseISO } from 'date-fns';
import type { Schedule, Milestone } from '@/types/schedule';

const MIN_LEAD_TIME_HOURS = 13;

export interface ScheduleValidation {
  valid: boolean;
  error?: string;
  earliestBakeTime?: string;
}

/**
 * Validates bake time and returns structured result with error details.
 */
export function validateBakeTime(bakeDateISO: string): ScheduleValidation {
  const bakeDate = new Date(bakeDateISO);
  const now = new Date();
  const hoursUntilBake = (bakeDate.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (bakeDate < now) {
    return { valid: false, error: 'Bake time is in the past.' };
  }

  if (hoursUntilBake < MIN_LEAD_TIME_HOURS) {
    const earliest = addHours(now, MIN_LEAD_TIME_HOURS);
    return {
      valid: false,
      error: `Need at least ${MIN_LEAD_TIME_HOURS} hours. Earliest: ${format(earliest, 'MMM d, h:mm a')}`,
      earliestBakeTime: earliest.toISOString(),
    };
  }

  return { valid: true };
}

/**
 * Backward schedule calculator using detailed recipe milestones.
 * Works backward from bread-ready time per sourdough-recipe.md:
 *
 * - Feed starter → counter rest 8-12 hours
 * - Mix dough with 1 cup starter
 * - First rise overnight on counter (8-12 hours)
 * - Punch down, knead, shape, into pans
 * - Second rise 4-5 hours
 * - Bake at 350°F for 20-25 minutes
 */
export function generateSchedule(bakeDateISO: string): Milestone[] {
  const breadReady = parseISO(bakeDateISO);

  const bakeStart = addMinutes(breadReady, -25);
  const secondRiseStart = addHours(bakeStart, -4.5);
  const kneadShape = addMinutes(secondRiseStart, -30);
  const firstRiseEnd = kneadShape;
  const mixDough = addHours(firstRiseEnd, -10);
  const feedStarter = addHours(mixDough, -12);

  return [
    {
      id: nanoid(),
      name: 'Feed Starter',
      description:
        'Feed with ¾ cup sugar, 1 cup warm water, 3 Tbsp instant potato flakes. Let stand on counter 8-12 hours.',
      time: feedStarter.toISOString(),
      completed: false,
      order: 0,
    },
    {
      id: nanoid(),
      name: 'Mix Dough',
      description:
        'Mix 1 cup starter, ¼ cup sugar, ½ cup corn oil, 2 tsp salt, 1½ cups warm water, 6 cups bread flour. Place in greased bowl, cover.',
      time: mixDough.toISOString(),
      completed: false,
      order: 1,
    },
    {
      id: nanoid(),
      name: 'Overnight Rise',
      description: 'Let dough rise covered overnight on counter (8-12 hours).',
      time: mixDough.toISOString(),
      completed: false,
      order: 2,
    },
    {
      id: nanoid(),
      name: 'Punch Down, Knead & Shape',
      description:
        'Punch down, knead 10 times. Divide into 3 parts, knead each 10-12 times on floured surface. Place in greased pans.',
      time: kneadShape.toISOString(),
      completed: false,
      order: 3,
    },
    {
      id: nanoid(),
      name: 'Second Rise',
      description:
        'Cover with plastic wrap. Let rise 4-5 hours or overnight. Tip: put in oven with light on.',
      time: secondRiseStart.toISOString(),
      completed: false,
      order: 4,
    },
    {
      id: nanoid(),
      name: 'Bake',
      description: 'Bake at 350°F for 20-25 minutes.',
      time: bakeStart.toISOString(),
      completed: false,
      order: 5,
    },
    {
      id: nanoid(),
      name: 'Bread Ready! 🍞',
      description: 'Remove from oven and let cool. Enjoy!',
      time: breadReady.toISOString(),
      completed: false,
      order: 6,
    },
  ];
}

/**
 * Creates a full Schedule object for a bake date.
 */
export function createSchedule(bakeDateISO: string): Schedule {
  const milestones = generateSchedule(bakeDateISO);

  return {
    id: nanoid(),
    bakeDate: bakeDateISO,
    milestones,
    archived: false,
    createdAt: new Date().toISOString(),
    syncStatus: 'local',
    source: 'app',
  };
}

/**
 * Gets the next uncompleted milestone.
 */
export function getNextMilestone(schedule: Schedule): Milestone | undefined {
  return schedule.milestones
    .sort((a, b) => a.order - b.order)
    .find((m) => !m.completed);
}

/**
 * Formats a milestone time for display.
 */
export function formatMilestoneTime(isoTime: string): string {
  return format(parseISO(isoTime), 'EEE, MMM d — h:mm a');
}
